import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../contexts/AuthContext';
import { CaraLoomLogo } from '../components/brand/CaraLoomLogo';
import { Seo } from '../components/seo/Seo';
import { organizationSchema, breadcrumbSchema } from '../lib/seoSchemas';
import { Sparkles, Search, ArrowLeft, Send, Loader2, MessageSquare, BookOpen, Mail, ShieldCheck, Wand2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { label: 'Getting started', prompts: ['How do I book my first visit?', 'How does CaraLoom verify clinicians?', 'How do I add a family member?'] },
  { label: 'Payments & billing', prompts: ['How does verified-settlement work?', 'When does the clinician get paid?', 'How do I add a payment method?'] },
  { label: 'Visits & scheduling', prompts: ['How do I reschedule?', 'What if my clinician is late?', 'Can I cancel a visit?'] },
  { label: 'Telehealth', prompts: ['How do I start a telehealth call?', 'My camera isn\'t working', 'Can I record the visit?'] },
  { label: 'For clinicians', prompts: ['How do I get paid out?', 'How do I update my rate?', 'How do I document a visit?'] },
];

export default function HelpCenter() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, busy]);

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message) return;
    const next = [...messages, { role: 'user', content: message }];
    setMessages(next); setInput(''); setBusy(true);
    try {
      const endpoint = token ? `${API}/lynk/chat` : `${API}/lynk/login-help`;
      const payload = token
        ? { message, session_id: sessionId, page_route: '/help', page_title: 'CaraLoom Help', mode: 'general' }
        : { message, session_id: sessionId };
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(endpoint, payload, { headers });
      setSessionId(res.data.session_id);
      setMessages([...next, { role: 'assistant', content: res.data.response }]);
    } catch (err) {
      setMessages([...next, { role: 'assistant', content: "I had a hiccup. Try rephrasing — or browse the categories on the left." }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-cn-bg dark:bg-[#020617] relative overflow-hidden" data-testid="help-center">
      <Seo
        title="Help Center | CaraLoom — Ask Cara Anything"
        description="Get instant answers about booking in-home care, verified clinicians, payments, telehealth, and scheduling on CaraLoom. Talk to a human anytime."
        path="/help"
        schema={[organizationSchema(), breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Help', path: '/help' }])]}
      />
      <div className="pointer-events-none fixed -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-cn-secondary/15 blur-[120px] animate-cn-aurora" />
      <div className="pointer-events-none fixed -bottom-40 -right-32 w-[32rem] h-[32rem] rounded-full bg-cn-accent/10 blur-[140px] animate-cn-aurora" style={{ animationDelay: '4s' }} />

      <header className="relative z-20 cn-glass border-b border-cn-border/60 sticky top-0">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-cn-border/40" data-testid="help-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <CaraLoomLogo />
            <div className="hidden sm:flex items-center gap-2 ml-4 pl-4 border-l border-cn-border/60">
              <BookOpen className="w-4 h-4 text-cn-primary" />
              <span className="text-sm font-semibold text-cn-text">Help Center</span>
            </div>
          </div>
          <a href="mailto:care@caraloom.com" className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-cn-text-muted hover:text-cn-primary" data-testid="help-contact-btn">
            <Mail className="w-4 h-4" /> Talk to a human
          </a>
        </div>
      </header>

      <main className="relative z-10 max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 py-10">
        {/* Hero */}
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <p className="cn-eyebrow">Ask Cara anything</p>
          <h1 className="cn-display text-4xl sm:text-5xl text-cn-text dark:text-white mt-2">How can we help?</h1>
          <p className="text-cn-text-muted mt-3">Type your question below — no menus, no maze. Cara knows CaraLoom inside and out.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Categories rail */}
          <aside className="lg:col-span-4 space-y-4">
            <div className="cn-card p-5">
              <p className="cn-eyebrow">Browse by topic</p>
              <div className="mt-3 space-y-3">
                {CATEGORIES.map((cat, i) => (
                  <div key={i}>
                    <p className="text-sm font-bold text-cn-text dark:text-white mb-1.5">{cat.label}</p>
                    <div className="space-y-1">
                      {cat.prompts.map((p, j) => (
                        <button
                          key={j}
                          onClick={() => send(p)}
                          data-testid={`help-prompt-${i}-${j}`}
                          className="block w-full text-left text-xs px-3 py-2 rounded-xl bg-cn-bg dark:bg-white/5 border border-cn-border/40 hover:border-cn-primary/40 hover:bg-cn-primary/5 text-cn-text-muted hover:text-cn-text transition-colors"
                        >
                          → {p}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="cn-card p-5 text-white border-transparent" style={{ background: 'linear-gradient(135deg, #0F172A, #334155)' }}>
              <ShieldCheck className="w-5 h-5 mb-2 opacity-80" />
              <p className="font-display font-bold text-base">Need a human?</p>
              <p className="text-xs text-white/85 mt-1 mb-3">Safety issues are prioritized ahead of all other requests — we respond within one business day.</p>
              <a href="mailto:care@caraloom.com" className="inline-flex items-center gap-2 text-sm font-semibold underline" data-testid="help-email-link">
                <Mail className="w-4 h-4" /> care@caraloom.com
              </a>
            </div>
          </aside>

          {/* Chat */}
          <section className="lg:col-span-8">
            <div className="cn-card flex flex-col h-[60vh] min-h-[420px] overflow-hidden">
              <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-cn-bg/40 dark:bg-[#020617]/40">
                {messages.length === 0 && (
                  <div className="text-center py-10 max-w-md mx-auto">
                    <div className="w-16 h-16 mx-auto rounded-3xl flex items-center justify-center text-white mb-3" style={{ background: 'linear-gradient(135deg, #0F172A, #334155)' }}>
                      <Sparkles className="w-8 h-8" />
                    </div>
                    <p className="cn-display text-xl text-cn-text dark:text-white">Ask me anything</p>
                    <p className="text-sm text-cn-text-muted mt-2">Try "How do I reschedule a visit?" or pick a topic on the left.</p>
                  </div>
                )}
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[82%] rounded-2xl px-4 py-3 text-sm ${
                      m.role === 'user' ? 'bg-cn-primary text-white rounded-br-md' : 'bg-white dark:bg-white/5 text-cn-text dark:text-white border border-cn-border/60 rounded-bl-md'
                    }`}>
                      {m.role === 'assistant' ? (
                        <div className="prose prose-sm dark:prose-invert max-w-none">
                          <ReactMarkdown components={{
                            p: ({ children }) => <p className="text-sm mb-2 last:mb-0 leading-relaxed">{children}</p>,
                            a: ({ href, children }) => <Link to={href || '#'} className="text-cn-primary font-semibold underline">{children}</Link>,
                            ul: ({ children }) => <ul className="list-disc pl-5 space-y-1 my-2">{children}</ul>,
                            ol: ({ children }) => <ol className="list-decimal pl-5 space-y-1 my-2">{children}</ol>,
                            strong: ({ children }) => <strong className="font-semibold text-cn-primary">{children}</strong>,
                          }}>{m.content}</ReactMarkdown>
                        </div>
                      ) : <p className="whitespace-pre-wrap">{m.content}</p>}
                    </div>
                  </div>
                ))}
                {busy && <div className="flex items-center gap-2 text-sm text-cn-text-muted px-2"><Loader2 className="w-4 h-4 animate-spin text-cn-primary" /> Cara is thinking…</div>}
                <div ref={bottomRef} />
              </div>
              <div className="p-4 border-t border-cn-border/60 bg-white dark:bg-[#0F172A]">
                <div className="flex items-center gap-2 bg-cn-bg dark:bg-white/5 border border-cn-border/60 rounded-2xl p-1.5 focus-within:border-cn-primary/40">
                  <Search className="w-4 h-4 ml-2 text-cn-text-muted" />
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && send()}
                    placeholder="Ask in your own words..."
                    data-testid="help-input"
                    className="flex-1 bg-transparent outline-none text-sm text-cn-text dark:text-white placeholder:text-cn-text-muted"
                    disabled={busy}
                  />
                  <button onClick={() => send()} disabled={busy || !input.trim()} data-testid="help-send" className="p-2 rounded-xl text-white disabled:opacity-50" style={{ background: 'linear-gradient(135deg, #0F172A, #334155)' }}>
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
